/**
 * Analytics Repository Factory Function
 *
 * Production-ready repository for business intelligence & feature adoption.
 * Follows factory function pattern with Apollo Client injection.
 *
 * Features:
 * - 7 queries (dashboard, categories, thresholds, time series)
 * - 6 mutations (create, record, update, enable/disable, recalculate)
 * - 3 subscriptions (threshold breach, value updated, trend changed)
 * - Real-time alerting and trend analysis
 *
 * @see graphql_gateway/src/analytics_api/ - Backend implementation
 * @see .claude/25_PRIMITIVE_MIGRATION_MASTER_PLAN.md - Position #22/25 (OPTIMIZE phase)
 */

import { ApolloClient } from '@apollo/client';
import {
  GqlAnalytics,
  AnalyticsConnection,
  AnalyticsDashboard,
  TimeSeriesDataPoint,
  CreateAnalyticsInput,
  RecordMetricDataPointInput,
  MetricCategory,
} from './types';
import {
  GET_ANALYTICS,
  LIST_ANALYTICS,
  GET_ANALYTICS_BY_CATEGORY,
  GET_ANALYTICS_WITH_THRESHOLD_BREACHES,
  GET_ANALYTICS_BY_SOURCE_PRIMITIVE,
  GET_ANALYTICS_DASHBOARD,
  GET_TIME_SERIES_DATA,
} from './queries';
import {
  CREATE_ANALYTICS,
  RECORD_METRIC_DATA_POINT,
  UPDATE_ANALYTICS_THRESHOLDS,
  SET_ANALYTICS_ENABLED,
  RECALCULATE_ANALYTICS,
  CLEAR_ANALYTICS_DATA,
} from './mutations';

/**
 * Analytics repository interface (7Q + 6M pattern)
 */
export interface AnalyticsRepositoryV2 {
  // ===== Queries (7) =====
  getAnalytics(analyticsId: string): Promise<GqlAnalytics | null>;
  listAnalytics(tenantId: string, limit?: number, cursor?: string): Promise<AnalyticsConnection>;
  getAnalyticsByCategory(tenantId: string, category: MetricCategory): Promise<GqlAnalytics[]>;
  getAnalyticsWithThresholdBreaches(tenantId: string): Promise<GqlAnalytics[]>;
  getAnalyticsBySourcePrimitive(tenantId: string, sourcePrimitiveType: string): Promise<GqlAnalytics[]>;
  getAnalyticsDashboard(tenantId: string): Promise<AnalyticsDashboard>;
  getTimeSeriesData(analyticsId: string, startTime: string, endTime: string): Promise<TimeSeriesDataPoint[]>;

  // ===== Mutations (6) =====
  createAnalytics(input: CreateAnalyticsInput): Promise<GqlAnalytics>;
  recordMetricDataPoint(input: RecordMetricDataPointInput): Promise<GqlAnalytics>;
  updateAnalyticsThresholds(
    analyticsId: string,
    warningThreshold?: number,
    criticalThreshold?: number
  ): Promise<GqlAnalytics>;
  setAnalyticsEnabled(analyticsId: string, enabled: boolean): Promise<GqlAnalytics>;
  recalculateAnalytics(analyticsId: string): Promise<GqlAnalytics>;
  clearAnalyticsData(analyticsId: string): Promise<GqlAnalytics>;
}

/**
 * Create Analytics repository with Apollo Client
 *
 * @example
 * ```typescript
 * const client = new ApolloClient({ ... });
 * const analyticsRepo = createAnalyticsRepositoryV2(client);
 *
 * // Get dashboard overview
 * const dashboard = await analyticsRepo.getAnalyticsDashboard('tenant-123');
 *
 * // Find threshold breaches (critical alerts)
 * const alerts = await analyticsRepo.getAnalyticsWithThresholdBreaches('tenant-123');
 *
 * // Track performance metric
 * const metric = await analyticsRepo.createAnalytics({
 *   tenantId: 'tenant-123',
 *   analyticsName: 'API Response Time',
 *   analyticsCode: 'API_RESPONSE_TIME',
 *   description: 'P95 API response time in milliseconds',
 *   sourcePrimitiveType: 'Queue',
 *   sourcePrimitiveIds: ['queue-123'],
 *   metricType: MetricType.HISTOGRAM,
 *   metricName: 'response_time_ms',
 *   metricUnit: MetricUnit.MILLISECONDS,
 *   metricCategory: MetricCategory.PERFORMANCE,
 *   aggregationType: AggregationType.PERCENTILE,
 *   warningThreshold: 500, // 500ms warning
 *   criticalThreshold: 1000, // 1s critical
 * });
 *
 * // Get time series for visualization
 * const timeSeries = await analyticsRepo.getTimeSeriesData(
 *   metric.analyticsId,
 *   '2024-01-01T00:00:00Z',
 *   '2024-01-31T23:59:59Z'
 * );
 * ```
 */
export function createAnalyticsRepositoryV2(
  client: ApolloClient
): AnalyticsRepositoryV2 {
  return {
    // ===== Queries (7) =====

    async getAnalytics(analyticsId: string): Promise<GqlAnalytics | null> {
      const { data } = await client.query<Record<string, any>>({
        query: GET_ANALYTICS,
        variables: { analyticsId },
        fetchPolicy: 'network-only',
      });
      return data?.getAnalytics ?? null;
    },

    async listAnalytics(
      tenantId: string,
      limit?: number,
      cursor?: string
    ): Promise<AnalyticsConnection> {
      const { data } = await client.query<Record<string, any>>({
        query: LIST_ANALYTICS,
        variables: { tenantId, limit, cursor },
        fetchPolicy: 'network-only',
      });
      return data?.listAnalytics ?? null;
    },

    async getAnalyticsByCategory(
      tenantId: string,
      category: MetricCategory
    ): Promise<GqlAnalytics[]> {
      const { data } = await client.query<Record<string, any>>({
        query: GET_ANALYTICS_BY_CATEGORY,
        variables: { tenantId, category },
        fetchPolicy: 'network-only',
      });
      return data?.getAnalyticsByCategory ?? null;
    },

    async getAnalyticsWithThresholdBreaches(tenantId: string): Promise<GqlAnalytics[]> {
      const { data } = await client.query<Record<string, any>>({
        query: GET_ANALYTICS_WITH_THRESHOLD_BREACHES,
        variables: { tenantId },
        fetchPolicy: 'network-only',
      });
      return data?.getAnalyticsWithThresholdBreaches ?? null;
    },

    async getAnalyticsBySourcePrimitive(
      tenantId: string,
      sourcePrimitiveType: string
    ): Promise<GqlAnalytics[]> {
      const { data } = await client.query<Record<string, any>>({
        query: GET_ANALYTICS_BY_SOURCE_PRIMITIVE,
        variables: { tenantId, sourcePrimitiveType },
        fetchPolicy: 'network-only',
      });
      return data?.getAnalyticsBySourcePrimitive ?? null;
    },

    async getAnalyticsDashboard(tenantId: string): Promise<AnalyticsDashboard> {
      const { data } = await client.query<Record<string, any>>({
        query: GET_ANALYTICS_DASHBOARD,
        variables: { tenantId },
        fetchPolicy: 'network-only',
      });
      return data?.getAnalyticsDashboard ?? null;
    },

    async getTimeSeriesData(
      analyticsId: string,
      startTime: string,
      endTime: string
    ): Promise<TimeSeriesDataPoint[]> {
      const { data } = await client.query<Record<string, any>>({
        query: GET_TIME_SERIES_DATA,
        variables: { analyticsId, startTime, endTime },
        fetchPolicy: 'network-only',
      });
      return data?.getTimeSeriesData ?? null;
    },

    // ===== Mutations (6) =====

    async createAnalytics(input: CreateAnalyticsInput): Promise<GqlAnalytics> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: CREATE_ANALYTICS,
        variables: { input },
      });
      return data?.createAnalytics ?? null;
    },

    async recordMetricDataPoint(input: RecordMetricDataPointInput): Promise<GqlAnalytics> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: RECORD_METRIC_DATA_POINT,
        variables: { input },
      });
      return data?.recordMetricDataPoint ?? null;
    },

    async updateAnalyticsThresholds(
      analyticsId: string,
      warningThreshold?: number,
      criticalThreshold?: number
    ): Promise<GqlAnalytics> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: UPDATE_ANALYTICS_THRESHOLDS,
        variables: { analyticsId, warningThreshold, criticalThreshold },
      });
      return data?.updateAnalyticsThresholds ?? null;
    },

    async setAnalyticsEnabled(analyticsId: string, enabled: boolean): Promise<GqlAnalytics> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: SET_ANALYTICS_ENABLED,
        variables: { analyticsId, enabled },
      });
      return data?.setAnalyticsEnabled ?? null;
    },

    async recalculateAnalytics(analyticsId: string): Promise<GqlAnalytics> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: RECALCULATE_ANALYTICS,
        variables: { analyticsId },
      });
      return data?.recalculateAnalytics ?? null;
    },

    async clearAnalyticsData(analyticsId: string): Promise<GqlAnalytics> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: CLEAR_ANALYTICS_DATA,
        variables: { analyticsId },
      });
      return data?.clearAnalyticsData ?? null;
    },
  };
}
